import type { BoatPalette } from './boatPalette';

/** A hand position in the craft's own coordinate space. */
export type HandPoint = readonly [number, number];

/**
 * The seated figure shared by the kayak and the jet ski, so the two riders
 * match.
 *
 * Hips sit at (`hipX`, `hipY`); the torso leans by `lean` radians; the arms
 * reach out to two hand points given in the craft's coordinate space (not the
 * leaned torso's), which is why the shoulder is projected back out of the
 * rotation before the arms are drawn.
 */
export const drawRider = (
  palette: BoatPalette,
  hipX: number,
  hipY: number,
  lean: number,
  handA: HandPoint,
  handB: HandPoint,
): void => {
  const { ctx } = palette;
  const c = (hex: string): string => palette.colour(hex);

  const skin = c('C8895C');
  const top = c('2E6E8E');
  const hat = c('22303A');
  // lifejacket: signal-orange PFD over darker seams
  const pfd = c('FF5C39');
  const pfdSeam = c('B93A1D');

  ctx.save();
  ctx.translate(hipX, hipY);
  ctx.rotate(lean);

  ctx.fillStyle = top;
  ctx.beginPath();
  ctx.moveTo(-5, 1);
  ctx.quadraticCurveTo(-6, -11, -1, -14);
  ctx.quadraticCurveTo(5, -13, 5, -2);
  ctx.quadraticCurveTo(5, 1, 0, 1.5);
  ctx.closePath();
  ctx.fill();

  // The vest sits over the torso, slightly inset so a sliver of shirt shows at
  // the shoulders. Sleeveless, so the arms keep the shirt colour. The collar
  // sits at the neck and is mostly hidden behind the head, reading as the
  // classic PFD ring.
  ctx.fillStyle = pfd;
  ctx.beginPath();
  ctx.moveTo(-4.5, 0.5);
  ctx.quadraticCurveTo(-5.4, -10.2, -1, -13);
  ctx.quadraticCurveTo(4.5, -12, 4.5, -1.6);
  ctx.quadraticCurveTo(4.5, 0.6, 0, 1);
  ctx.closePath();
  ctx.fill();
  ctx.strokeStyle = pfdSeam;
  ctx.lineWidth = 0.5;
  ctx.globalAlpha = 0.8;
  ctx.stroke();
  ctx.globalAlpha = 1;
  ctx.strokeStyle = pfdSeam;
  ctx.lineWidth = 0.6;
  ctx.globalAlpha = 0.7;
  ctx.beginPath();
  ctx.moveTo(-4.9, -4.2);
  ctx.quadraticCurveTo(0, -3.2, 4.3, -4.4);
  ctx.moveTo(-5.1, -8);
  ctx.quadraticCurveTo(0, -7, 4.4, -8.2);
  ctx.stroke();
  ctx.globalAlpha = 1;

  ctx.fillStyle = pfd;
  ctx.beginPath();
  ctx.ellipse(1, -14.2, 3.2, 1.6, 0, 0, Math.PI * 2);
  ctx.fill(); // collar

  ctx.fillStyle = skin;
  ctx.beginPath();
  ctx.arc(1, -18, 3.4, 0, Math.PI * 2);
  ctx.fill(); // head

  ctx.fillStyle = hat;
  ctx.beginPath();
  ctx.arc(1, -19, 3.7, Math.PI, 0);
  ctx.fill();
  ctx.fillRect(1, -19.6, 5.2, 1.6); // cap + brim toward the bow

  ctx.restore();

  // Shoulder position in the craft's space, so the arms can reach world-space
  // hand points regardless of the torso lean.
  const shoulderX = hipX + Math.cos(lean) + 13 * Math.sin(lean);
  const shoulderY = hipY + Math.sin(lean) - 13 * Math.cos(lean);

  ctx.strokeStyle = top;
  ctx.lineWidth = 2.8;
  ctx.lineCap = 'round';

  const arm = (hand: HandPoint): void => {
    const midX = (shoulderX + hand[0]) / 2;
    const midY = (shoulderY + hand[1]) / 2;

    ctx.beginPath();
    ctx.moveTo(shoulderX, shoulderY);
    ctx.quadraticCurveTo(midX, midY + 3.2, hand[0], hand[1]); // elbow drop
    ctx.stroke();
  };

  arm(handA);
  arm(handB);
  ctx.lineCap = 'butt';

  ctx.fillStyle = skin;
  ctx.beginPath();
  ctx.arc(handA[0], handA[1], 1.6, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  ctx.arc(handB[0], handB[1], 1.6, 0, Math.PI * 2);
  ctx.fill();
};
